import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilePlus2, Search, Paperclip, RotateCcw, Building2, FileText, Wallet, Receipt, Sigma } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Money, StatusBadge, EmptyState, Pager, Spinner, StatCard } from "@/components/common";
import SearchSelect from "@/components/SearchSelect";
import BillDrawer from "@/components/BillDrawer";
import { api, cleanParams, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import PeriodBar, { rangeFor } from "@/components/PeriodBar";
import { useAuth } from "@/context/AuthContext";

const TITLES = {
  super_admin: ["Purchases", "Purchase bill history", "Select a client to view its purchase bills. Corrections preserve prior values; voids replace deletion."],
  staff: ["Purchases", "Purchase bill history", "Select one of your assigned clients to view its purchase bills."],
  auditor: ["Purchases", "Purchase bill history", "Read-only access to authorised client records and their source documents."],
};

const PAGE = 500;
const PERIODS = ["today", "month", "last_month", "last3", "last6", "fy", "last_fy", "custom"];

const Sel = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value || "all"} onValueChange={onChange}>
    <SelectTrigger className="h-9 text-sm" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-white max-h-72">
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function Bills() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [fy0, fy1] = rangeFor("fy");
  const init = { q: "", client_id: sp.get("client_id") || "", vendor_id: "", staff_id: sp.get("staff_id") || "",
    record_status: "", invoice_from: fy0, invoice_to: fy1, missing_attachment: sp.get("missing") === "1", mine: false };
  const [flt, setFlt] = useState(init);
  const [period, setPeriod] = useState("fy");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(null);
  const [eyebrow, title, subtitle] = TITLES[user.role];

  useEffect(() => {
    api.get("/clients").then(({ data: d }) => setClients(d));
    api.get("/vendors", { params: { limit: 1000 } }).then(({ data: d }) => setVendors(d));
    if (user.role === "super_admin") api.get("/users", { params: { role: "staff" } }).then(({ data: d }) => setStaff(d));
  }, [user.role]);

  const load = useCallback(async () => {
    if (!flt.client_id) { setData(null); return; }
    try {
      const { data: d } = await api.get("/bills", { params: { ...cleanParams(flt), page, page_size: PAGE } });
      setData(d);
    } catch (e) { toast.error(formatErr(e)); }
  }, [flt, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => { setFlt((p) => ({ ...p, [k]: v === "all" ? "" : v })); setPage(1); };
  const pickPeriod = (p) => {
    setPeriod(p);
    if (p !== "custom") { const [a, b] = rangeFor(p); setFlt((x) => ({ ...x, invoice_from: a, invoice_to: b })); setPage(1); }
  };
  const client = clients.find((c) => c.id === flt.client_id);

  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle}
        actions={user.role !== "auditor" && <Button onClick={() => nav("/bills/new")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="bills-add-button"><FilePlus2 className="w-4 h-4 mr-1.5" />Add purchase bill</Button>} />

      <div className="panel p-4 mb-4 fade-up space-y-4" data-testid="bill-filters">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-[380px]">
            <div className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-emerald-700" />Client company <span className="text-red-500">*</span></div>
            <SearchSelect value={flt.client_id} onChange={(v) => set("client_id", v)} options={clients.map((c) => [c.id, `${c.company_name} · ${c.code}`])} placeholder="Type to search client…" testid="bill-filter-client" />
          </div>
          {client && <div className="text-xs text-slate-500 num pb-2" data-testid="bill-history-client-meta">VAT {client.vat_number}{client.primary_staff_name ? ` · In-charge: ${client.primary_staff_name}` : ""}</div>}
        </div>
        <PeriodBar periods={PERIODS} period={period} from={flt.invoice_from} to={flt.invoice_to} onPeriod={pickPeriod} onFrom={(v) => set("invoice_from", v)} onTo={(v) => set("invoice_to", v)} />
        {flt.client_id && <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
          <div className="relative lg:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={flt.q} onChange={(e) => set("q", e.target.value)} className="pl-9 h-9" placeholder="Search invoice no., vendor, item, VAT no., sponsor, record ID…" data-testid="bill-register-search-input" />
          </div>
          <SearchSelect value={flt.vendor_id} onChange={(v) => set("vendor_id", v)} placeholder="All vendors — type to search" options={vendors.map((c) => [c.id, c.name])} testid="bill-filter-vendor" />
          <Sel value={flt.record_status} onChange={(v) => set("record_status", v)} placeholder="Active & voided" options={[["active", "Active only"], ["voided", "Voided only"]]} testid="bill-filter-record-status" />
          {user.role === "super_admin" && <SearchSelect value={flt.staff_id} onChange={(v) => set("staff_id", v)} placeholder="All staff — type to search" options={staff.map((s) => [s.id, s.name])} testid="bill-filter-staff" />}
          <div className="flex items-end gap-4 pb-1.5">
            <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={flt.missing_attachment} onCheckedChange={(v) => set("missing_attachment", v)} data-testid="bill-filter-missing" />Missing attachment</label>
            {user.role === "staff" && <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={flt.mine} onCheckedChange={(v) => set("mine", v)} data-testid="bill-filter-mine" />Only mine</label>}
          </div>
          <div className="flex items-end"><Button variant="ghost" size="sm" onClick={() => { setFlt({ ...init, client_id: flt.client_id, staff_id: "", missing_attachment: false }); setPeriod("fy"); setPage(1); }} data-testid="bill-filter-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset filters</Button></div>
        </div>}
      </div>

      {!flt.client_id ? (
        <div className="panel fade-up"><EmptyState title="Select a client to view bill history" subtitle="History is shown per client. Default period is the current financial year." testid="bill-history-select-client" /></div>
      ) : (<>
      {data && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4" data-testid="bill-history-summary">
          <StatCard label="Total bills" value={data.total.toLocaleString("en-US")} icon={FileText} testid="summary-total-bills" />
          <StatCard label="Amount (before VAT)" value={`${fmtMoney(data.sums.before_vat)}`} hint="SAR" icon={Wallet} tone="slate" testid="summary-amount" delay={50} />
          <StatCard label="VAT" value={`${fmtMoney(data.sums.vat_paid)}`} hint="SAR" icon={Receipt} tone="amber" testid="summary-vat" delay={100} />
          <StatCard label="Total amount" value={`${fmtMoney(data.sums.total)}`} hint="SAR" icon={Sigma} testid="summary-total-amount" delay={150} />
        </div>
      )}
      <div className="panel overflow-hidden fade-up" style={{ animationDelay: "60ms" }}>
        {data && (
          <div className="flex flex-wrap gap-x-8 gap-y-2 px-4 py-3 border-b border-slate-200 bg-slate-50/60 text-sm" data-testid="bill-register-totals">
            <span className="text-slate-500"><span className="num font-semibold text-slate-900">{data.total}</span> records · {PAGE} per page</span>
            <span className="text-slate-500">Before VAT <Money value={data.sums.before_vat} /></span>
            <span className="text-slate-500">VAT <Money value={data.sums.vat_paid} /></span>
            <span className="text-slate-500">Total <Money value={data.sums.total} strong /></span>
          </div>
        )}
        <div className="overflow-x-auto">
          {!data ? <Spinner /> : !data.items.length ? <EmptyState subtitle="No bills for this client in the selected period." /> : (
            <table className="w-full" data-testid="bill-register-table">
              <thead><tr>{["Record", "Invoice date", "Invoice no.", "Client", "Vendor", "Item", "Before VAT", "VAT", "Total", "Status", "Entered"].map((h) => <th key={h} className={`th ${["Before VAT", "VAT", "Total"].includes(h) ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
              <tbody>
                {data.items.map((b, i) => (
                  <tr key={b.id} className={`row-hover cursor-pointer ${b.record_status === "voided" ? "opacity-60" : ""}`} onClick={() => setOpen(b.id)} data-testid={`bill-row-${i}`}>
                    <td className="td num text-xs text-slate-500">{b.record_no}</td>
                    <td className="td num text-xs">{fmtDate(b.invoice_date)}</td>
                    <td className="td num text-xs font-medium text-slate-800">{b.invoice_number}</td>
                    <td className="td max-w-[180px] truncate">{b.client_name}</td>
                    <td className="td max-w-[180px]"><div className="truncate">{b.vendor_name}</div><div className="num text-[11px] text-slate-400">{b.vendor_vat_number}</div></td>
                    <td className="td max-w-[140px] truncate text-slate-600">{b.item_name}</td>
                    <td className="td text-right"><Money value={b.before_vat} /></td>
                    <td className="td text-right"><Money value={b.vat_paid} /></td>
                    <td className="td text-right"><Money value={b.total} strong /></td>
                    <td className="td">
                      <div className="flex items-center gap-1.5">
                        <StatusBadge status={b.record_status} />
                        {b.attachment_count ? <span className="text-[11px] text-slate-400 flex items-center"><Paperclip className="w-3 h-3" />{b.attachment_count}</span> : <StatusBadge status="missing" />}
                      </div>
                    </td>
                    <td className="td text-xs"><div className="text-slate-700">{b.created_by_name}</div><div className="num text-[11px] text-slate-400">{fmtDateTime(b.created_at)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {data && data.total > 0 && <Pager page={page} pageSize={PAGE} total={data.total} onPage={setPage} />}
      </div>
      </>)}
      <BillDrawer billId={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} onChanged={load} />
    </div>
  );
}
